import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, RefreshCw, ScrollText, Sparkles, Newspaper } from "lucide-react";
import api from "../api";
import ClanLogo from "../components/clan/ClanLogo";

const KIND_STYLE = {
  admin: "bg-gold-500/15 text-gold-400 border-gold-500/30",
  "match-start": "bg-destructive/15 text-rose-400 border-destructive/30",
  "match-score": "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  "match-final": "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  transfer_market: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  system_profile: "bg-purple-500/15 text-purple-300 border-purple-500/30",
};

function fmtDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("ar");
  } catch {
    return value;
  }
}

function KindBadge({ kind }) {
  const cls = KIND_STYLE[kind] || "bg-white/10 text-white/70 border-white/20";
  return (
    <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-wider ${cls}`}>
      {kind || "news"}
    </span>
  );
}

function TransferClan({ clan, fallback }) {
  const logoSrc = clan?.logo_url || clan?.avatar || clan?.logo;
  return (
    <div className="flex items-center gap-3 min-w-0 bg-black/30 border border-white/5 rounded-2xl p-3">
      <ClanLogo
        clan={{ logo: logoSrc }}
        className="h-11 w-11 rounded-xl bg-gold-500/10 border border-gold-500/20 grid place-items-center text-gold-500 overflow-hidden shrink-0 shadow-sm"
        fallbackIconSize={20}
      />
      <div className="min-w-0">
        <div className="font-bold text-sm text-white truncate">{clan?.name || fallback}</div>
        <div className="text-xs font-mono text-gold-400/80 mt-0.5">[{clan?.tag || "---"}]</div>
      </div>
    </div>
  );
}

export default function NewsTransfersPage() {
  const [tab, setTab] = useState("news");
  const [news, setNews] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [newsRes, transferRes] = await Promise.all([
        api.get("/news", { params: { limit: 100 } }),
        api.get("/transfers", { params: { limit: 100 } }),
      ]);
      setNews(newsRes.data || []);
      setTransfers(transferRes.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const headline = useMemo(() => {
    const latest = news[0];
    if (!latest) return "تابع آخر أخبار المنصة والانتقالات لحظة بلحظة";
    return latest.title;
  }, [news]);

  return (
    <div className="space-y-8" dir="rtl">
      {/* ── Header ── */}
      <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#111113]/90 p-6 md:p-8 shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-l from-gold-500/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-500 text-xs font-bold mb-1">
              <Newspaper size={14} /> التغطية الإعلامية
            </div>
            <h1 className="font-display font-black text-3xl md:text-4xl text-white flex items-center gap-3">
              الأخبار وسوق الانتقالات
            </h1>
            <p className="text-white/60 text-xs md:text-sm max-w-2xl leading-relaxed">{headline}</p>
          </div>
          <button
            onClick={() => load()}
            className="px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-xs text-white font-bold inline-flex items-center gap-2 transition shadow-md"
          >
            <RefreshCw size={14} /> تحديث البيانات
          </button>
        </div>
      </section>

      {/* ── Tabs Selector ── */}
      <div className="flex justify-end">
        <div className="w-full sm:w-auto">
          <div className="inline-flex items-center bg-[#111113]/90 border border-white/10 rounded-2xl p-1.5 shadow-xl">
            <button
              onClick={() => setTab("news")}
              className={`px-6 py-2.5 rounded-xl text-xs font-bold inline-flex items-center gap-2 transition-all duration-300 ${
                tab === "news"
                  ? "bg-gold-500 text-black font-black shadow-[0_0_15px_rgba(255,204,0,0.3)]"
                  : "text-white/50 hover:text-white hover:bg-white/5"
              }`}
            >
              <ScrollText size={15} /> موجز الأخبار ({news.length})
            </button>
            <button
              onClick={() => setTab("transfers")}
              className={`px-6 py-2.5 rounded-xl text-xs font-bold inline-flex items-center gap-2 transition-all duration-300 ${
                tab === "transfers"
                  ? "bg-gold-500 text-black font-black shadow-[0_0_15px_rgba(255,204,0,0.3)]"
                  : "text-white/50 hover:text-white hover:bg-white/5"
              }`}
            >
              <ArrowRightLeft size={15} /> سوق الانتقالات ({transfers.length})
            </button>
          </div>
  
        </div>
      </div>

      {/* ── Content Area ── */}
      {loading ? (
        <div className="grid gap-3">
          <div className="h-24 rounded-2xl bg-[#111113]/50 border border-white/5 animate-pulse" />
          <div className="h-24 rounded-2xl bg-[#111113]/50 border border-white/5 animate-pulse" />
          <div className="h-24 rounded-2xl bg-[#111113]/50 border border-white/5 animate-pulse" />
        </div>
      ) : tab === "news" ? (
        <section className="space-y-4">
          {news.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-[#111113]/90 p-12 text-center text-white/40 text-xs">لا توجد أخبار منشورة حالياً.</div>
          ) : news.map((item) => (
            <article key={item.id} className="rounded-2xl border border-white/10 bg-[#111113]/90 p-5 shadow-xl transition hover:border-gold-500/30">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <KindBadge kind={item.kind} />
                <span className="text-xs font-mono text-white/40">{fmtDate(item.created_at)}</span>
              </div>
              <h3 className="text-base md:text-lg font-black font-display text-white">{item.title}</h3>
              {item.body ? <p className="mt-2 text-xs md:text-sm text-white/70 leading-relaxed">{item.body}</p> : null}
              {item.kind === "match-score" && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 px-2.5 py-1 rounded-lg">
                  <Sparkles size={12} /> تحديث مباشر للنتيجة
                </div>
              )}
            </article>
          ))}
        </section>
      ) : (
        <section className="space-y-4">
          {transfers.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-[#111113]/90 p-12 text-center text-white/40 text-xs">لا توجد انتقالات مسجلة في سوق الانتقالات حالياً.</div>
          ) : transfers.map((t) => (
            <article key={t.id} className="rounded-2xl border border-white/10 bg-[#111113]/90 p-5 shadow-xl transition hover:border-gold-500/30">
              <div className="flex items-start justify-between gap-3 flex-wrap border-b border-white/5 pb-3">
                <div>
                  <h3 className="font-display font-black text-lg text-white">{t.username || "لاعب محترف"}</h3>
                  <p className="text-xs text-white/50 mt-0.5">فترة التواجد في النادي السابق: <span className="text-gold-400 font-bold">{t.duration_label || "—"}</span></p>
                </div>
                <span className="text-xs font-mono text-white/40">{fmtDate(t.created_at)}</span>
              </div>

              <div className="mt-4 grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                <TransferClan clan={t.old_clan} fallback="النادي السابق" />
                <div className="mx-auto h-10 w-10 rounded-xl border border-gold-500/30 bg-gold-500/10 grid place-items-center text-gold-500 shadow-sm">
                  <ArrowRightLeft size={16} />
                </div>
                <TransferClan clan={t.new_clan} fallback="النادي الجديد" />
              </div>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
